import { useEffect, useRef, useCallback } from 'react';
import { serializeUrlState, parseUrlState } from '../utils/urlState.utils';
/**
 * Hook to synchronize lister URL state with the browser URL
 *
 * 1. Syncs state changes to URL (pushState)
 * 2. Handles popstate for back/forward navigation
 */
export const useProductListerUrlSync = (urlState, options) => {
    const { skipInitialPush = false, onPopState, defaults } = options;
    // Track if this is the first render
    const isFirstRender = useRef(true);
    // Track the previous URL state to avoid unnecessary pushes
    const previousUrlStateRef = useRef(null);
    /**
     * Push the current URL state to browser history
     */
    const pushUrlState = useCallback((state) => {
        if (typeof window === 'undefined') {
            return;
        }
        const searchString = serializeUrlState(state);
        const newUrl = `${window.location.pathname}${searchString ? `?${searchString}` : ''}`;
        // Store the full URL state in history.state for popstate restoration
        window.history.pushState({ listerUrlState: state }, '', newUrl);
        previousUrlStateRef.current = searchString;
    }, []);
    /**
     * Replace the current URL in browser history (used after initial navigation)
     */
    const replaceUrlState = useCallback((state) => {
        if (typeof window === 'undefined') {
            return;
        }
        const searchString = serializeUrlState(state);
        const newUrl = `${window.location.pathname}${searchString ? `?${searchString}` : ''}`;
        window.history.replaceState({ listerUrlState: state }, '', newUrl);
        previousUrlStateRef.current = searchString;
    }, []);
    // Handle popstate (browser back/forward)
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const handlePopState = (event) => {
            var _a;
            const searchParams = new URLSearchParams(window.location.search);
            const newState = ((_a = event.state) === null || _a === void 0 ? void 0 : _a.listerUrlState) || parseUrlState(searchParams, defaults);
            // Update the ref BEFORE calling onPopState to prevent the sync effect from pushing again
            previousUrlStateRef.current = serializeUrlState(newState);
            onPopState === null || onPopState === void 0 ? void 0 : onPopState(newState);
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [onPopState, defaults]);
    // Sync URL state changes to browser URL
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const currentSearchString = serializeUrlState(urlState);
        // Skip if URL hasn't changed
        if (currentSearchString === previousUrlStateRef.current) {
            return;
        }
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (skipInitialPush) {
                // Just update the ref without pushing
                previousUrlStateRef.current = currentSearchString;
                // Replace state to ensure history.state has our data
                replaceUrlState(urlState);
                return;
            }
        }
        // Push the new URL state
        pushUrlState(urlState);
    }, [urlState, skipInitialPush, pushUrlState, replaceUrlState]);
    return {
        pushUrlState,
        replaceUrlState,
    };
};
